import { Loader, LoaderWrapper } from '@src/components/Loader';
import { useBalances } from '@src/hooks/useBalances';
import {
  BlotterDensity,
  BlotterTable,
  Checkbox,
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  IndicatorBadge,
  InlineFormattedNumber,
  LoaderSizes,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useBlotterTable,
  useConstant,
  useMixpanel,
  useObservable,
  useObservableValue,
  usePersistedBlotterTable,
  useSecurityDefaultIncrement,
  type BuyingPower,
} from '@talos/kyoko';
import type { ColDef, GridOptions, ICellRendererParams, RowClassParams } from 'ag-grid-enterprise';
import type Big from 'big.js';
import { compact, flatten, values } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { ReplaySubject } from 'rxjs';
import { useTheme } from 'styled-components';
import type { Balance, BalancesFilter } from '../../../types';
import { ErrorBoundary } from '../../ErrorBoundary';
import { BodyText, MarketAccountCheckbox, MarketsWrapper } from '../styles';
import type {
  InnerMarketAccountSelectionListProps,
  MarketAccountObject,
  MarketAccountSelectionGridProps,
  MarketAccountSelectionProps,
} from '../types';
import { getBuyingPowerSum, getMarketAccountBalance } from '../utils';
import type { MaoRow } from './MaoRow';
import { useColumns } from './useColumns';

enum MarketTypeEnum {
  Exchanges = 'Exchanges',
  Dealers = 'Dealers',
}
export interface SelectionGridProps
  extends MarketAccountSelectionProps, InnerMarketAccountSelectionListProps, MarketAccountSelectionGridProps {}

export const SelectionGrid: React.FC<SelectionGridProps> = props => {
  const {
    baseBuyingPower,
    quoteBuyingPower,

    security,
    exchangeChecked,
    exchangeIndeterminate,
    handleExchangeCheckbox,
    dealerChecked,
    dealerIndeterminate,
    handleDealerCheckbox,
    exchangeMarketObjGroups,
    dealerMarketObjGroups,
    handleMarketAccountChange,
    handleMarketChange,
    selectedExchangeMarkets,
    selectedDealerMarkets,
    selectableDealerMarketAccountObjs,
    selectableExchangeMarketAccountObjs,
  } = props;
  const mixpanel = useMixpanel();

  const baseCurrency = security?.BaseCurrency ?? '';
  const quoteCurrency = security?.QuoteCurrency ?? '';
  const baseCurrencyIncrement = useSecurityDefaultIncrement({ security, isCcy: false });
  const quoteCurrencyIncrement = useSecurityDefaultIncrement({ security, isCcy: true });

  const theme = useTheme();
  const balanceDeltaFilter: BalancesFilter = useMemo(() => {
    return {
      Currencies: [baseCurrency, quoteCurrency],
      MarketAccounts: compact([
        ...flatten(values(exchangeMarketObjGroups)).map(mktObject => mktObject.marketAccount?.Name),
        ...flatten(values(dealerMarketObjGroups)).map(mktObject => mktObject.marketAccount?.Name),
      ]),
    };
  }, [baseCurrency, quoteCurrency, exchangeMarketObjGroups, dealerMarketObjGroups]);

  const { balancesByMarketAccountIDCurrencyObs } = useBalances({
    filter: balanceDeltaFilter,
    tag: 'SelectionGrid',
    openStreamWithoutFilter: false,
  });
  const balancesByMarketAccountIDCurrency = useObservableValue(
    () => balancesByMarketAccountIDCurrencyObs,
    [balancesByMarketAccountIDCurrencyObs]
  );

  const selectedCombined = useMemo(
    () => selectedExchangeMarkets.concat(selectedDealerMarkets),
    [selectedExchangeMarkets, selectedDealerMarkets]
  );

  const innerRenderer = useCallback(
    (params: ICellRendererParams<MaoRow>) => {
      if (params.node.level === 0) {
        if (params.value === MarketTypeEnum.Dealers) {
          return (
            <SectionHeader
              label={params.value}
              checked={dealerChecked}
              handleCheckbox={handleDealerCheckbox}
              indeterminate={dealerIndeterminate}
              selectedCount={selectedDealerMarkets.length}
              selectableCount={selectableDealerMarketAccountObjs.length}
            />
          );
        } else {
          return (
            <SectionHeader
              label={params.value}
              checked={exchangeChecked}
              handleCheckbox={handleExchangeCheckbox}
              indeterminate={exchangeIndeterminate}
              selectedCount={selectedExchangeMarkets.length}
              selectableCount={selectableExchangeMarketAccountObjs.length}
            />
          );
        }
      } else {
        if (params.value && params.node.data?.mao) {
          const mao = params.node.data.mao;
          const isSelected = selectedCombined.includes(mao.name);
          return (
            <MarketAccountCheckbox>
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                    [MixpanelEventProperty.Source]: MixpanelEventSource.MarketsPanel,
                  });
                  mao.isSelected = isSelected;
                  mao.marketAccount ? handleMarketAccountChange(mao) : handleMarketChange(mao);
                }}
              >
                <BodyText selected={isSelected}>{mao.label}</BodyText>
              </Checkbox>
            </MarketAccountCheckbox>
          );
        } else {
          return 'Total selected';
        }
      }
    },
    [
      dealerChecked,
      handleDealerCheckbox,
      dealerIndeterminate,
      selectedDealerMarkets.length,
      selectableDealerMarketAccountObjs.length,
      exchangeChecked,
      handleExchangeCheckbox,
      exchangeIndeterminate,
      selectedExchangeMarkets.length,
      selectableExchangeMarketAccountObjs.length,
      selectedCombined,
      mixpanel,
      handleMarketAccountChange,
      handleMarketChange,
    ]
  );

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: '',
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      width: 200,
      field: 'name',
      cellRendererParams: {
        suppressCount: true,
      },
      cellRendererSelector: params => ({
        component: innerRenderer,
        params,
      }),
    } satisfies ColDef<MaoRow>;
  }, [innerRenderer]);

  const defaultColDef = useConstant({
    cellRendererParams: {
      theme,
    },
    cellRendererSelector: params => {
      if (params.node.rowPinned) {
        return { component: (params: ICellRendererParams<MaoRow>) => <>{params.value}</> };
      } else {
        return undefined;
      }
    },
    resizable: true,
    width: 120,
    sortable: false,
  } satisfies ColDef<MaoRow>);

  const getRowClass = useCallback((params: RowClassParams) => {
    const result: string[] = [];
    if (params.data?.inGroup) {
      result.push('in-group');
    }
    if (params.data?.firstInGroup) {
      result.push('first-in-group');
    }
    if (params.data?.lastInGroup) {
      result.push('last-in-group');
    }
    return result;
  }, []);

  const gridOptions = useMemo((): GridOptions => {
    return {
      maintainColumnOrder: true,
      // Selection is custom handled in this component
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
      groupDefaultExpanded: -1,
      suppressRowDrag: true,
      getRowClass,
      defaultColDef,
      autoGroupColumnDef,
    };
  }, [getRowClass, defaultColDef, autoGroupColumnDef]);

  const setPinnedTopRowData = useCallback(() => {
    const baseBp = getBuyingPowerSum(baseBuyingPower, selectedCombined);
    const quoteBp = getBuyingPowerSum(quoteBuyingPower, selectedCombined);

    return [
      {
        name: 'Total selected',
        unsettledBase: '-',
        unsettledQuote: '-',
        maxBuyBase: NumericHeaderRow({
          number: baseBp.availableBuy,
          currency: baseCurrency,
          currencyIncrement: baseCurrencyIncrement,
        }),
        maxBuyQuote: NumericHeaderRow({
          number: quoteBp.availableBuy,
          currency: quoteCurrency,
          currencyIncrement: quoteCurrencyIncrement,
        }),
        maxSellBase: NumericHeaderRow({
          number: baseBp.availableSell,
          currency: baseCurrency,
          currencyIncrement: baseCurrencyIncrement,
        }),
        maxSellQuote: NumericHeaderRow({
          number: quoteBp.availableSell,
          currency: quoteCurrency,
          currencyIncrement: quoteCurrencyIncrement,
        }),
      },
    ];
  }, [
    baseCurrency,
    quoteCurrency,
    baseCurrencyIncrement,
    quoteCurrencyIncrement,
    baseBuyingPower,
    quoteBuyingPower,
    selectedCombined,
  ]);

  const dataSubject = useConstant(new ReplaySubject<any>(1));
  const dataObservable = useObservable(() => dataSubject.asObservable(), [dataSubject]);

  const { columns } = useColumns({ baseCurrency, quoteCurrency });
  const persistence = usePersistedBlotterTable(`markets-grid-${security?.Symbol}`);
  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'id',
    density: BlotterDensity.Compact,
    persistence,
    gridOptions,
    columns,
  });

  useEffect(() => {
    if (blotterTable.gridApi) {
      let items: MaoRow[] = getMaos({
        maos: exchangeMarketObjGroups,
        baseCurrency,
        baseCurrencyIncrement,
        quoteCurrency,
        quoteCurrencyIncrement,
        baseBuyingPower,
        quoteBuyingPower,
        type: MarketTypeEnum.Exchanges,
        balancesByMarketAccountIDCurrency,
      });
      items = items.concat(
        getMaos({
          maos: dealerMarketObjGroups,
          baseCurrency,
          baseCurrencyIncrement,
          quoteCurrency,
          quoteCurrencyIncrement,
          baseBuyingPower,
          quoteBuyingPower,
          type: MarketTypeEnum.Dealers,
          balancesByMarketAccountIDCurrency,
        })
      );
      dataSubject.next({ data: items });

      blotterTable.gridApi.setGridOption('pinnedTopRowData', setPinnedTopRowData());
    }
  }, [
    dataSubject,
    baseCurrency,
    baseCurrencyIncrement,
    quoteCurrency,
    quoteCurrencyIncrement,
    exchangeMarketObjGroups,
    dealerMarketObjGroups,
    baseBuyingPower,
    quoteBuyingPower,
    theme,
    balancesByMarketAccountIDCurrency,
    setPinnedTopRowData,
    blotterTable.gridApi,
  ]);

  if (
    security == null ||
    (Object.keys(exchangeMarketObjGroups).length === 0 && Object.keys(dealerMarketObjGroups).length === 0)
  ) {
    return (
      <LoaderWrapper>
        <Loader size={LoaderSizes.TINY} />
      </LoaderWrapper>
    );
  }

  return (
    <ErrorBoundary>
      <MarketsWrapper>
        <BlotterTable {...blotterTable} />
      </MarketsWrapper>
    </ErrorBoundary>
  );
};

interface GetMaosParams {
  maos: { [key: string]: MarketAccountObject[] };
  baseCurrency: string;
  baseCurrencyIncrement?: string;
  quoteCurrency: string;
  quoteCurrencyIncrement?: string;
  baseBuyingPower: Map<string, BuyingPower> | undefined;
  quoteBuyingPower: Map<string, BuyingPower> | undefined;
  type: MarketTypeEnum;
  balancesByMarketAccountIDCurrency: Map<number, Map<string, Balance>> | undefined;
}

function getMaos({
  maos,
  baseCurrency,
  baseCurrencyIncrement,
  quoteCurrency,
  quoteCurrencyIncrement,
  baseBuyingPower,
  quoteBuyingPower,
  type,
  balancesByMarketAccountIDCurrency,
}: GetMaosParams): MaoRow[] {
  const result: MaoRow[] = [];
  for (const key of Object.keys(maos)) {
    const items = maos[key];
    items.forEach((mao, index) => {
      const baseBpo = baseBuyingPower?.get(mao.name);
      const quoteBpo = quoteBuyingPower?.get(mao.name);

      const rowItem: MaoRow = {
        type: type,
        id: mao.name,
        name: mao.label,
        market: mao.market.Name,
        unsettledBase:
          type === MarketTypeEnum.Dealers
            ? getMarketAccountBalance(
                balancesByMarketAccountIDCurrency,
                mao?.marketAccount?.MarketAccountID,
                baseCurrency
              )
            : '',
        unsettledQuote:
          type === MarketTypeEnum.Dealers
            ? getMarketAccountBalance(
                balancesByMarketAccountIDCurrency,
                mao?.marketAccount?.MarketAccountID,
                quoteCurrency
              )
            : '',
        maxBuyBase: baseBpo?.AvailableBuy,
        maxBuyQuote: quoteBpo?.AvailableBuy,
        maxSellBase: baseBpo?.AvailableSell,
        maxSellQuote: quoteBpo?.AvailableSell,
        mao,
        inGroup: items.length > 1,
        firstInGroup: index === 0,
        lastInGroup: index === items.length - 1,
        baseCurrency,
        baseCurrencyIncrement,
        quoteCurrency,
        quoteCurrencyIncrement,
      };
      result.push(rowItem);
    });
  }
  return result;
}

interface SectionHeaderProps {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  handleCheckbox: (checked: boolean) => void;
  selectedCount: number;
  selectableCount: number;
}

function SectionHeader({
  label,
  checked,
  indeterminate,
  handleCheckbox,
  selectedCount,
  selectableCount,
}: SectionHeaderProps) {
  return (
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => handleCheckbox(!checked)}>
      <BodyText selected={checked}>
        {label}
        <IndicatorBadge children={`${selectedCount} / ${selectableCount}`} />
      </BodyText>
    </Checkbox>
  );
}

interface NumericHeaderRowProps {
  number: Big;
  currency: string;
  currencyIncrement: string | undefined;
}

function NumericHeaderRow({ number, currency, currencyIncrement }: NumericHeaderRowProps) {
  return (
    <>
      {!number.eq(0) ? (
        <InlineFormattedNumber number={number} increment={currencyIncrement} currency={currency} />
      ) : (
        '-'
      )}
    </>
  );
}
